/**
 * Whether the player is outdoors, and what the yard sounds like from there.
 *
 * The counterpart of `karaoke.ts`, and deliberately its mirror image: that one
 * is a thing standing in a room you are running *towards*, this one is the room
 * you are running *out of*. Both come down to the same two numbers - how much
 * reaches you and how much top is left of it - so both return `ListenerMix`,
 * and the mixer follows either without knowing which is which.
 *
 * Pure, for the same reason `karaoke.ts` and `pace.ts` are: the interesting
 * part is the arithmetic, not the node graph, and geometry is worth testing
 * without a browser.
 *
 * Presentation only. Nothing here is reachable from `src/sim`.
 */

import { YARD } from '../config/audio';
import type { ListenerMix } from './karaoke';

/**
 * Under a roof - any roof.
 *
 * The pirtti counts as much as the navetta: the run starts in a bedroom, and a
 * bedroom with the yard's birds at full level in it is a bedroom with no walls.
 */
export function indoors(x: number, z: number): boolean {
  return YARD.roofs.some(
    (roof) => x >= roof.minX && x <= roof.maxX && z >= roof.minZ && z <= roof.maxZ,
  );
}

/**
 * How the yard reaches the listener at (x, z).
 *
 * No distance term, unlike the karaoke: the birds are not in one tree, they are
 * the whole outside. Either you are in it or there is a wall between you and
 * it, and the wall is the only thing there is to hear.
 */
export function yardMix(x: number, z: number): ListenerMix {
  const wall = indoors(x, z) ? YARD.inside : YARD.outside;
  return { gain: wall.gain, cutoff: wall.cutoff };
}

/**
 * What the mixer plays before the game has said where anybody is standing.
 *
 * The indoor one, like `SILENT_MIX` next door: a default that is too quiet is
 * corrected on the first rendered frame, and a default that is too loud is a
 * yard full of birds in somebody's bedroom for that frame.
 */
export const UNPLACED_MIX: ListenerMix = { gain: YARD.inside.gain, cutoff: YARD.inside.cutoff };
